import { useCallback, useEffect, useRef } from "react"
import type { URLSearchParamsInit } from "react-router"
import { useSearchParams as useRRSearchParams } from "react-router"
import {
  isLocationDetailsTab,
  type LocationDetailsTab,
} from "../components/LocationDetails/LocationDetailsTabs"
import { TRUE } from "../utils/constants"
import { removeNullAndUndefined } from "../utils/helpers"

export type SetSearchParams = ReturnType<
  typeof useSearchParams
>["setSearchParams"]

interface Params {
  id?: string
  mapboxId?: string
  title?: string
  subtitle?: string
  organization_id?: string
  organization_name?: string
  user_id?: string
  userName?: string
  asset_id?: string
  fitBounds?: boolean
  centroid?: string
  affectedByCriticalThreat?: "true" | undefined
  assetType?: string
  group?: string
  searchText?: string
  tab?: LocationDetailsTab
  newChatMessage?: string
}

export const useSearchParams = () => {
  const [searchParams, setRRSearchParams] = useRRSearchParams()

  // set function was causing map to rerender... we can't have that
  // potential footgun: race condition if you call this too soon 🤷
  const setSearchRef = useRef(setRRSearchParams)
  useEffect(() => {
    setSearchRef.current = setRRSearchParams
  }, [setRRSearchParams])

  const setSearchParams = useCallback((newParams: Partial<Params> | null) => {
    if (!newParams) {
      setSearchRef.current(undefined)
      return
    }
    const currentParams = Object.fromEntries(
      new URLSearchParams(window.location.search)
    )
    const equal = Object.entries(newParams).every(
      ([key, value]) => currentParams[key] === value
    )
    if (equal) return
    setSearchRef.current(
      removeNullAndUndefined({
        ...newParams,
      }) as URLSearchParamsInit,
      {
        replace: true,
      }
    )
  }, [])

  const tab = searchParams.get("tab")
  const locationDetailsTab = isLocationDetailsTab(tab) ? tab : undefined

  const params: Params = {
    id: searchParams.get("id") ?? undefined,
    mapboxId: searchParams.get("mapboxId") ?? undefined,
    title: searchParams.get("title") ?? undefined,
    subtitle: searchParams.get("subtitle") ?? undefined,
    organization_id: searchParams.get("organization_id") ?? undefined,
    organization_name: searchParams.get("organization_name") ?? undefined,
    user_id: searchParams.get("user_id") ?? undefined,
    userName: searchParams.get("userName") ?? undefined,
    asset_id: searchParams.get("asset_id") ?? undefined,
    fitBounds: searchParams.get("fitBounds") === TRUE ? true : undefined,
    centroid: searchParams.get("centroid") ?? undefined,
    affectedByCriticalThreat:
      searchParams.get("affectedByCriticalThreat") === TRUE ? TRUE : undefined,
    assetType: searchParams.get("assetType") ?? undefined,
    group: searchParams.get("group") ?? undefined,
    searchText: searchParams.get("searchText") ?? undefined,
    tab: locationDetailsTab,
    newChatMessage: searchParams.get("newChatMessage") ?? undefined,
  }

  return {
    ...params,
    setSearchParams,
    params,
  }
}
